import React, { useEffect, useMemo, useState } from 'react';
import { SpecialistStatistics, OngoingConsultationCard, QuickActionCard, RecentPatients, RecentFeedback, NoConsultationCard, SpecialistWelcomeModal } from '@/components';
import { Text } from '@/ui-kits';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Routes } from '@/navigation/Routes';
import {
  specialistDashboardRating,
  specialistDashboardTotalConsultationCase,
  specialistDashboardTotalPendingCase,
  specialistDashboardTotalOngoingCount,
  specialistDashboardOngoingConsultations,
  specialistDashboardPendingCase,
  specialistDashboardRecentPatients,
  specialistDashboardRecentFeedback,
  authDataSelector,
  specialistDashboardFailureStatus,
  specialistDashboardSuccessStatus,
  hasSpecialistConsultedBeforeSelector,
} from '@/services/redux/selectors';
import { formatName, resetMessage } from '@/utils/common_functions';
import { resetSpecialistDashboardMessage, specialistDashboardStatsRequest } from '@/services/redux/slices/specialistDashboardSlice';
import { ErrorCodes, getErrorMessage, SuccessCodes } from '@/utils/getMessages';
import { useToast } from '@/hooks';
import { Images } from '@/constants/defaults';
import { SpecialistProfileStatus } from '@/constants';

const SpecialistDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const rating = useSelector(specialistDashboardRating);
  const totalConsultationCase = useSelector(specialistDashboardTotalConsultationCase);
  const totalPendingCase = useSelector(specialistDashboardTotalPendingCase);
  const totalOngoingCount = useSelector(specialistDashboardTotalOngoingCount);
  const ongoingConsultations = useSelector(specialistDashboardOngoingConsultations);
  const authData = useSelector(authDataSelector);
  const pendingCase = useSelector(specialistDashboardPendingCase);
  const recentPatients = useSelector(specialistDashboardRecentPatients);
  const recentFeedback = useSelector(specialistDashboardRecentFeedback);

  const failure = useSelector(specialistDashboardFailureStatus) as ErrorCodes;
  const success = useSelector(specialistDashboardSuccessStatus) as SuccessCodes;
  const hasConsulted = useSelector(hasSpecialistConsultedBeforeSelector);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const onboardingStorageKey = useMemo(() => {
    return authData?.email_id ? `heal:specialist-onboarding:${authData.email_id}` : null;
  }, [authData?.email_id]);

  function gotoConsultationList() {
    navigate(Routes.CONSULTATION);
  }

  /**
   * side effects
   */

  // useEffect to handle failure and success messages
  useEffect(() => {
    if (failure) {
      showToast(getErrorMessage(failure), 'failure');
    }

    return () => resetMessage(() => dispatch(resetSpecialistDashboardMessage()));
  }, [failure, success]);

  // fetch dashboard stats
  useEffect(() => {
    dispatch(specialistDashboardStatsRequest());
  }, []);

  useEffect(() => {
    const profileStatus = (authData as any)?.profile_status as SpecialistProfileStatus | undefined;
    // Only show modal if profile_status is NotStarted (0)
    if (!onboardingStorageKey || hasConsulted || totalConsultationCase === null || profileStatus !== SpecialistProfileStatus.NotStarted) {
      return;
    }
    const hasSeenModal = localStorage.getItem(onboardingStorageKey);
    if (!hasSeenModal) {
      setShowWelcomeModal(true);
    }
  }, [hasConsulted, onboardingStorageKey, totalConsultationCase, authData]);

  const closeWelcomeModal = () => {
    if (onboardingStorageKey) {
      localStorage.setItem(onboardingStorageKey, 'true');
    }
    setShowWelcomeModal(false);
  };

  const goToProfileFromModal = () => {
    navigate(Routes.PROFILE);
    closeWelcomeModal();
  };

  const dashboardContent = hasConsulted ? (
    <div className="px-3 sm:px-4 md:px-6 py-4 flex flex-col gap-4 sm:gap-6">
      <Text variant="headingSemiBold" className="gradient-text text-2xl sm:text-3xl lg:text-4xl">
        Welcome, Dr. {formatName(authData?.username ?? '')}
      </Text>

      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        <SpecialistStatistics value={totalConsultationCase ?? 0} icon="medical_record" title="Total Consultations" />
        <SpecialistStatistics value={totalPendingCase ?? 0} icon="diagnosis" title="Pending Opinions" />
        <SpecialistStatistics variant="dark" value={rating ?? 0} icon="star_big" title="Average Rating" />
      </div>

      <div className="flex items-center justify-between mt-4 sm:mt-8">
        <Text variant="headingMedium" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-xl">
          New Consultations
        </Text>
        {totalOngoingCount > 2 && (
          <button onClick={gotoConsultationList} className="text-ui_blue font-FigtreeSemiBold text-sm sm:text-md underline hover:opacity-80 transition-all gradient-text uppercase">
            VIEW ALL
          </button>
        )}
      </div>
      {ongoingConsultations.length === 0 ? (
        <div className="flex flex-col shadow-lg items-center justify-center h-full bg-white rounded-xl">
          <img src={Images.no_ongoing_consultation} alt="No Consultation" className="w-1/2 h-auto" />
          <Text variant="subtitle" className="text-ui_input_border_dark max-w-[700px] text-center mb-5 text-lg font-semibold">
            You don’t have any active consultations at the moment. New cases will appear here once patients request your expertise.
          </Text>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4">
          {ongoingConsultations.map(consultation => (
            <OngoingConsultationCard key={consultation.id} {...consultation} />
          ))}
        </div>
      )}

      <div className="mt-6 sm:mt-12 border-2 border-gray-300 rounded-xl p-4 sm:p-6 bg-white shadow-sm">
        <Text variant="headingMedium" className="text-ui_blue font-FigtreeSemiBold mb-1 text-lg sm:text-xl">
          Quick Actions
        </Text>

        {/* Little More Big size and small as per Quick Actions Section*/}
        <Text variant="medium" className="text-ui_input_border_dark mb-4 sm:mb-5 text-base sm:text-lg font-semibold">
          Review Pending Second Opinions
        </Text>

        <div className="flex flex-col gap-3 sm:gap-3.5">
          {pendingCase.length === 0 ? (
            <Text variant="body" className="text-ui_input_border_dark mb-4 sm:mb-5 text-base sm:text-lg font-semibold">
              There are No Pending Second Opinions
            </Text>
          ) : (
            pendingCase.map(pc => <QuickActionCard key={`quick-action-${pc.id}`} {...pc} />)
          )}
        </div>

        <div className="mt-4 sm:mt-5 text-left font-FigtreeSemiBold">
          <button onClick={gotoConsultationList} className="text-ui_blue font-FigtreeMedium text-sm sm:text-md underline hover:opacity-80 transition-all">
            View All Consultations
          </button>
        </div>
      </div>

      {/* Recent Patients and Feedback Section - Only show if there's data */}
      {(recentPatients.length > 0 || recentFeedback.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8">
          {recentPatients.length > 0 && <RecentPatients className={recentFeedback.length === 0 ? "col-span-full" : ""} patients={recentPatients} />}
          {recentFeedback.length > 0 && <RecentFeedback feedbacks={recentFeedback} />}
        </div>
      )}
    </div>
  ) : (
    <NoConsultationCard />
  );

  return (
    <>
      {dashboardContent}
      <SpecialistWelcomeModal
        open={showWelcomeModal}
        onClose={closeWelcomeModal}
        onGoToProfile={goToProfileFromModal}
        specialistName={authData?.username}
      />
    </>
  );
};

export default SpecialistDashboard;
